import { PartnerDTO } from 'support/types';
import { FilterValue, PropertyFilter, PropertyFilterType, SearchPropertyFilter } from 'support/types/filters';

import { deepCopy } from '../generic/generic';

export const addPartnersToFiltersConfig = (
  defaultFilterConfig: Array<PropertyFilter>,
  key: string,
  partners?: Array<PartnerDTO>,
): Array<PropertyFilter> => {
  const newFilterConfig = deepCopy<Array<PropertyFilter>>(defaultFilterConfig);

  // deepCopy (lodash cloneDeep) cannot clone the React component in `Icon`, so restore the original reference by key.
  newFilterConfig.forEach((filter: PropertyFilter) => {
    const original = defaultFilterConfig.find((config) => config.key === filter.key);
    if (original?.Icon) {
      filter.Icon = original.Icon;
    }
  });

  const filterIndex = newFilterConfig.findIndex((filter: PropertyFilter) => filter.key === key);

  if (filterIndex > -1) {
    const configDropDownOptions = newFilterConfig[filterIndex].dropDownOptions;

    if (configDropDownOptions) {
      configDropDownOptions.isLoading = !partners;

      if (partners) {
        configDropDownOptions.dropdownValues = partners.map((partner: PartnerDTO) => ({
          label: partner.name,
          value: partner.id,
        }));
      }
    }
  }

  return newFilterConfig;
};

// Short search terms match a large part of the data and are slow on the server, so the search waits for more input.
export const MIN_SEARCH_LENGTH = 3;

// Each pause in typing longer than this sends a request, so a longer delay sends fewer of them.
export const SEARCH_DEBOUNCE_MS = 400;

export const isSearchTermTooShort = (value: string, minLength: number | undefined): boolean => {
  const termLength = value.trim().length;

  return !!minLength && termLength > 0 && termLength < minLength;
};

// Removes search terms that are shorter than the filter's `minLength`, so they are not sent to the API.
export const withoutShortSearchTerms = (
  filterValues: Array<FilterValue>,
  propertyFilters: Array<PropertyFilter>,
): Array<FilterValue> =>
  filterValues.filter((filterValue) => {
    const searchFilter = propertyFilters.find(
      (filter): filter is SearchPropertyFilter =>
        filter.key === filterValue.key && filter.type === PropertyFilterType.search,
    );

    return !isSearchTermTooShort(filterValue.value, searchFilter?.minLength);
  });
